<script setup lang="ts">
import { type Breakpoints, useBreakpoints } from "@vueuse/core";
import { computed, inject, onMounted, ref } from "vue";
import { ThemeButton } from "@/ui-components";
import { storeToRefs } from "pinia";
import { useUiStore } from "@/stores/ui.ts";
import { useRoute } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import TrailingIcon from "@/assets/icons/trailing.svg?component";
import CloseIcon from "@/assets/icons/close.svg?component";
import ArrowUpIcon from "@/assets/icons/arrow-up.svg?component";

const breakpoints = useBreakpoints(inject("breakpoints") as Breakpoints<string>);
const uiStore = useUiStore();
const { filtersVisibility, isDropdownOpened } = storeToRefs(uiStore);
const route = useRoute();

const filtersInViewport = ref(true);
const root = ref<HTMLElement>();

onClickOutside(root, () => closeFilters());

const filtersIcon = computed(() => {
  return filtersInViewport.value
    ? filtersVisibility.value
      ? CloseIcon
      : TrailingIcon
    : ArrowUpIcon;
});

const closeFiltersText = computed(() => {
  return !(breakpoints.active().value === "md")
    ? filtersVisibility.value && !filtersInViewport.value
      ? "Filtern"
      : "Schließen"
    : "";
});

const closeFilters = () => {
  if (
    breakpoints.smallerOrEqual("sm").value &&
    route.name !== "search" &&
    route.name !== "lti-search"
  ) {
    filtersVisibility.value = false;
  }
};

const showFilters = async () => {
  document.querySelector(".simplebar-content-wrapper")?.scrollTo({ top: 0, behavior: "smooth" });
  if (filtersVisibility.value && !filtersInViewport.value) {
    return;
  }
  filtersVisibility.value = !filtersVisibility.value;
};

const filtersVisibilityHandler = (event: Event) => {
  filtersInViewport.value = (event as CustomEvent).detail.isVisible;
};

onMounted(() => {
  window.addEventListener("filtersVisibility", filtersVisibilityHandler);
});
</script>

<template>
  <div
    ref="root"
    class="header-search-manager"
    :class="{
      mdAndUp: breakpoints.greater('md').value,
      lgAndUp: breakpoints.greater('lg').value,
    }"
  >
    <SearchAutocomplete class="search-input me-1" />
    <div v-if="route.name === 'search' || route.name === 'lti-search'" class="manager-button">
      <ThemeButton
        v-if="filtersVisibility"
        :variant="isDropdownOpened ? 'light' : 'outline-primary'"
        title="Filtern"
        :icon-right="breakpoints.active().value === 'md' ? null : filtersIcon"
        :icon-center="breakpoints.active().value === 'md' ? filtersIcon : null"
        :width-icon="16"
        :height-icon="16"
        :fab="breakpoints.active().value === 'md'"
        aria-label="Filtern"
        @click="showFilters"
      >
        {{ closeFiltersText }}
      </ThemeButton>
      <ThemeButton
        v-else
        variant="outline-primary"
        title="Filtern"
        :icon-right="!breakpoints.greaterOrEqual('lg').value ? null : TrailingIcon"
        :icon-center="!breakpoints.greaterOrEqual('lg').value ? TrailingIcon : null"
        :width-icon="16"
        :height-icon="16"
        :fab="!breakpoints.greaterOrEqual('lg').value"
        aria-label="Filtern"
        @click="showFilters"
      >
        {{ breakpoints.greaterOrEqual("lg").value ? "Filtern" : "" }}
      </ThemeButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.header-search-manager {
  position: relative;
  display: flex;

  .search-input {
    position: static;
    width: 100%;

    :deep(.suggest-list) {
      position: absolute;
      top: 56px;
      right: 0;
      bottom: 0;
      left: 0;
    }
  }

  &.wide {
    margin-right: 0;
    flex: 1;
  }

  &.mdAndUp {
    .search-input {
      width: 256px;
      &.wide {
        width: 100%;
      }
    }
  }

  &.lgAndUp {
    .search-input {
      width: 384px;
      &.wide {
        width: 100%;
      }
    }
    .manager-button {
      min-width: auto;
    }
  }
}
</style>
